import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { CreateMemberGroupDialog } from "@/dialogs/member-group-create-dialog";
import type { cursos_slugTimelineMutation } from "./__generated__/cursos_slugTimelineMutation.graphql";
import type { cursos_slugTimelineQuery } from "./__generated__/cursos_slugTimelineQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/timeline",
)({
	component: RouteComponent,
});

type ReleaseType = "free" | "days" | "date";

type SectionRelease = {
	sectionId: string;
	sectionName: string;
	position: number;
	type: ReleaseType;
	daysToRelease: number | null;
	notAvailableBefore: string | null;
};

function getReleaseType(
	daysToRelease: number | null | undefined,
	notAvailableBefore: string | null | undefined,
): ReleaseType {
	if (notAvailableBefore) return "date";
	if (daysToRelease !== null && daysToRelease !== undefined) return "days";
	return "free";
}

function formatDateForInput(dateString: string | null | undefined): string {
	if (!dateString) return "";
	const date = new Date(dateString);
	return date.toISOString().split("T")[0];
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { slug } = Route.useParams();

	const data = useLazyLoadQuery<cursos_slugTimelineQuery>(
		graphql`
			query cursos_slugTimelineQuery($clubSlug: String!, $courseSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						name
						sections(status: ALL) {
							id
							name
							position
							active
						}
						memberGroups {
							id
							name
							isDefault
							releaseTimeline {
								section {
									id
								}
								daysToRelease
								notAvailableBefore
							}
						}
					}
				}
			}
		`,
		{ clubSlug: club?.slug ?? "", courseSlug: slug },
		{ fetchPolicy: "store-and-network" },
	);

	const [updateTimeline, isUpdating] =
		useMutation<cursos_slugTimelineMutation>(graphql`
		mutation cursos_slugTimelineMutation(
			$memberGroup: ID!
			$sectionsRelease: [SectionReleaseUpdateInput!]!
		) {
			courseSectionReleaseUpdate(
				memberGroup: $memberGroup
				sectionsRelease: $sectionsRelease
			) {
				id
				memberGroups {
					id
					releaseTimeline {
						section {
							id
						}
						daysToRelease
						notAvailableBefore
					}
				}
			}
		}
	`);

	const course = data.club.course;
	const memberGroups = course.memberGroups;
	const sections = [...course.sections].sort(
		(a, b) => (a.position ?? 0) - (b.position ?? 0),
	);

	const defaultGroup = memberGroups.find((g) => g.isDefault);
	const [selectedGroupId, setSelectedGroupId] = useState<string>(
		defaultGroup?.id ?? memberGroups[0]?.id ?? "",
	);

	const selectedGroup = memberGroups.find((g) => g.id === selectedGroupId);

	const buildInitialReleases = (): SectionRelease[] => {
		return sections.map((section) => {
			const timeline = selectedGroup?.releaseTimeline?.find(
				(t) => t.section.id === section.id,
			);

			return {
				sectionId: section.id,
				sectionName: section.name,
				position: section.position ?? 0,
				type: getReleaseType(
					timeline?.daysToRelease,
					timeline?.notAvailableBefore,
				),
				daysToRelease: timeline?.daysToRelease ?? null,
				notAvailableBefore: formatDateForInput(timeline?.notAvailableBefore),
			};
		});
	};

	const [releases, setReleases] = useState<SectionRelease[]>(
		buildInitialReleases(),
	);
	const [hasChanges, setHasChanges] = useState(false);
	const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);

	const handleGroupChange = (groupId: string) => {
		setSelectedGroupId(groupId);
		const group = memberGroups.find((g) => g.id === groupId);

		const newReleases = sections.map((section) => {
			const timeline = group?.releaseTimeline?.find(
				(t) => t.section.id === section.id,
			);

			return {
				sectionId: section.id,
				sectionName: section.name,
				position: section.position ?? 0,
				type: getReleaseType(
					timeline?.daysToRelease,
					timeline?.notAvailableBefore,
				),
				daysToRelease: timeline?.daysToRelease ?? null,
				notAvailableBefore: formatDateForInput(timeline?.notAvailableBefore),
			};
		});

		setReleases(newReleases);
		setHasChanges(false);
	};

	const updateRelease = (
		sectionId: string,
		updates: Partial<SectionRelease>,
	) => {
		setReleases((prev) =>
			prev.map((r) => (r.sectionId === sectionId ? { ...r, ...updates } : r)),
		);
		setHasChanges(true);
	};

	const handleTypeChange = (sectionId: string, newType: ReleaseType) => {
		updateRelease(sectionId, {
			type: newType,
			daysToRelease: newType === "days" ? 0 : null,
			notAvailableBefore: newType === "date" ? "" : null,
		});
	};

	const handleSave = () => {
		const sectionsRelease = releases.map((r) => ({
			section: r.sectionId,
			noReleaseRule: r.type === "free",
			daysToRelease: r.type === "days" ? r.daysToRelease : null,
			notAvailableBefore:
				r.type === "date" && r.notAvailableBefore
					? new Date(r.notAvailableBefore).toISOString()
					: null,
		}));

		updateTimeline({
			variables: {
				memberGroup: selectedGroupId,
				sectionsRelease,
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Cronograma atualizado com sucesso!");
				setHasChanges(false);
			},
			onError: (error) => {
				console.error(error);
				toast.error("Erro ao atualizar cronograma. Tente novamente.");
			},
		});
	};

	if (memberGroups.length === 0) {
		return (
			<div className="p-6">
				<div className="border rounded-md p-6 text-center">
					<Icon
						name="Users"
						className="size-12 text-muted-foreground mx-auto mb-4"
					/>
					<h3 className="text-lg font-semibold mb-2">
						Nenhuma turma encontrada
					</h3>
					<p className="text-muted-foreground">
						Crie uma turma para configurar o cronograma de liberação dos
						módulos.
					</p>
				</div>
			</div>
		);
	}

	if (sections.length === 0) {
		return (
			<div className="p-6">
				<div className="border rounded-md p-6 text-center">
					<Icon
						name="BookOpen"
						className="size-12 text-muted-foreground mx-auto mb-4"
					/>
					<h3 className="text-lg font-semibold mb-2">
						Nenhum módulo encontrado
					</h3>
					<p className="text-muted-foreground">
						Adicione módulos ao curso para configurar o cronograma de liberação.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="border rounded-md py-6 px-6 flex flex-col gap-6">
				<div className="flex flex-row items-center justify-between">
					<div className="flex flex-col gap-1">
						<CardTitle>Cronograma de Liberação</CardTitle>
						<p className="text-sm text-muted-foreground">
							Configure quando cada módulo será liberado para os alunos.
						</p>
					</div>

					<Button onClick={handleSave} disabled={!hasChanges || isUpdating}>
						{isUpdating && <Spinner />}
						Salvar
					</Button>
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-sm font-medium">Turma</span>
					<Select value={selectedGroupId} onValueChange={handleGroupChange}>
						<SelectTrigger className="w-full max-w-xs">
							<SelectValue placeholder="Selecione uma turma" />
						</SelectTrigger>
						<SelectContent>
							{memberGroups.map((group) => (
								<SelectItem key={group.id} value={group.id}>
									{group.name}
									{group.isDefault && " (Padrão)"}
								</SelectItem>
							))}
							<div className="border-t mt-1 pt-1">
								<button
									type="button"
									className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded flex items-center gap-1.5 text-primary"
									onClick={(e) => {
										e.stopPropagation();
										setIsCreateGroupDialogOpen(true);
									}}
								>
									<Icon name="Plus" className="size-3.5" />
									Criar turma
								</button>
							</div>
						</SelectContent>
					</Select>
				</div>

				<div className="bg-muted/60 rounded-md p-4 flex gap-3">
					<Icon
						name="Info"
						className="size-4 text-foreground shrink-0 mt-0.5"
					/>
					<div className="text-sm text-muted-foreground">
						<p className="font-medium text-foreground mb-1">
							Como funciona a liberação de módulos?
						</p>
						<ul className="list-disc list-inside space-y-1">
							<li>
								<strong>Acesso Livre:</strong> O módulo fica disponível
								imediatamente após a compra.
							</li>
							<li>
								<strong>Dias após compra:</strong> O módulo é liberado após um
								número específico de dias da data de compra do aluno.
							</li>
							<li>
								<strong>Data específica:</strong> O módulo é liberado em uma
								data fixa, igual para todos os alunos da turma.
							</li>
						</ul>
					</div>
				</div>

				<div className="border rounded-md overflow-hidden">
					<div className="bg-muted/50 px-4 py-3 border-b">
						<div className="grid grid-cols-[1fr_200px_200px] gap-4 text-sm font-medium">
							<span>Módulo</span>
							<span>Regra de Liberação</span>
							<span>Configuração</span>
						</div>
					</div>

					<div className="divide-y">
						{releases.map((release, index) => (
							<div
								key={release.sectionId}
								className="px-4 py-3 grid grid-cols-[1fr_200px_200px] gap-4 items-center"
							>
								<div className="flex items-center gap-3">
									<span className="text-muted-foreground text-sm w-6">
										{index + 1}.
									</span>
									<span className="text-sm font-medium">
										{release.sectionName}
									</span>
								</div>

								<Select
									value={release.type}
									onValueChange={(value) =>
										handleTypeChange(release.sectionId, value as ReleaseType)
									}
								>
									<SelectTrigger size="sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="free">
											<div className="flex items-center gap-2">
												<Icon name="LockOpen" className="size-3.5" />
												Acesso Livre
											</div>
										</SelectItem>
										<SelectItem value="days">
											<div className="flex items-center gap-2">
												<Icon name="Clock" className="size-3.5" />
												Dias após compra
											</div>
										</SelectItem>
										<SelectItem value="date">
											<div className="flex items-center gap-2">
												<Icon name="Calendar" className="size-3.5" />
												Data específica
											</div>
										</SelectItem>
									</SelectContent>
								</Select>

								<div className="flex items-center">
									{release.type === "free" && (
										<span className="text-sm text-muted-foreground">-</span>
									)}

									{release.type === "days" && (
										<div className="flex items-center gap-2">
											<Input
												type="number"
												min={0}
												value={release.daysToRelease ?? 0}
												onChange={(e) =>
													updateRelease(release.sectionId, {
														daysToRelease:
															Number.parseInt(e.target.value, 10) || 0,
													})
												}
												className="w-20 h-8"
											/>
											<span className="text-sm text-muted-foreground">
												dias
											</span>
										</div>
									)}

									{release.type === "date" && (
										<Input
											type="date"
											value={release.notAvailableBefore ?? ""}
											onChange={(e) =>
												updateRelease(release.sectionId, {
													notAvailableBefore: e.target.value,
												})
											}
											className="h-8"
										/>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			<CreateMemberGroupDialog
				course={{ id: course.id, name: course.name }}
				open={isCreateGroupDialogOpen}
				onClose={() => setIsCreateGroupDialogOpen(false)}
				onSuccess={(newGroup) => {
					setSelectedGroupId(newGroup.id);
					handleGroupChange(newGroup.id);
				}}
			/>
		</div>
	);
}
